// Password field with an in-field visibility toggle. The signup policy asks
// for a long passphrase, and typing one blind is where people mistype and
// bounce; the eye lets them check what they wrote. Visibility is per-field
// component state — never persisted, reset on unmount.

import { useState } from 'react';
import type { ComponentProps } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PasswordInput({
  showLabel,
  hideLabel,
  className,
  ...inputProps
}: Omit<ComponentProps<'input'>, 'type'> & {
  /** Localized toggle labels (aria-label + tooltip) for the two states. */
  showLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  // The swapping accessible name alone carries the toggle state (no
  // aria-pressed) so screen readers never announce "hide password, pressed".
  const toggleLabel = visible ? hideLabel : showLabel;
  return (
    <div className="relative">
      {/* className styles the inner input, not the wrapper div — pass border/
          size utilities, not flex-child ones. */}
      <input
        {...inputProps}
        type={visible ? 'text' : 'password'}
        className={cn(className, 'pr-11')}
      />
      <button
        type="button"
        data-slot="password-toggle"
        onClick={() => setVisible((current) => !current)}
        disabled={inputProps.disabled}
        aria-label={toggleLabel}
        title={toggleLabel}
        className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex min-w-11 items-center justify-center rounded-r-md disabled:opacity-50"
      >
        {visible ? (
          <EyeOffIcon className="size-4" aria-hidden="true" />
        ) : (
          <EyeIcon className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
